/**
 * =============================================================================
 * Componente: ContractFullPreview
 * Versão: 1.0.0 (2026-05-01)
 *
 * Descrição:
 *   Pré-visualização COMPLETA de um modelo de contrato, compondo:
 *     1. Capa (PDF, se houver)
 *     2. PDF base (se posição = 'before')
 *     3. Anexo HTML paginado (ContractTemplatePreview em modo embed)
 *     4. PDF base (se posição = 'after')
 *
 *   Reaproveitável em:
 *     - Modal "Regras da Campanha" (portal do afiliado)
 *     - Aba "Pré-visualização" do modelo de contrato (CRM)
 *     - Wizard de criação de contrato
 *     - Detalhe do contrato
 *
 *   Responsividade:
 *     Calcula `scale` dinamicamente via ResizeObserver no container externo
 *     e propaga para todos os blocos (capa, html, base) garantindo que a
 *     página A4 caiba sem scroll horizontal — incluindo em viewports mobile.
 * =============================================================================
 */
import { useEffect, useRef, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ContractTemplatePreview } from './ContractTemplatePreview';
import { ContractPdfPagePreview } from './ContractPdfPagePreview';
import { useContractAssetUrl } from '@/hooks/useContractAssetUrl';
import { getPreviewScaleFactor, PREVIEW_SCALE_FACTOR } from './contractStyles';

interface ContractFullPreviewProps {
  /** HTML do template (com variáveis {{chave}}) */
  contentHtml: string;
  /** URL/caminho da imagem de fundo (papel timbrado) */
  backgroundImagePath?: string | null;
  /** URL/caminho do PDF de capa (1ª página) */
  coverPdfPath?: string | null;
  /** URL/caminho do PDF base (cláusulas fixas) */
  basePdfPath?: string | null;
  /** Posição do PDF base em relação ao anexo HTML */
  basePdfPosition?: 'before' | 'after';
  /** Exibe número de página no PDF do anexo HTML */
  showPageNumbers?: boolean;
  /** Posição da paginação: 'top-right' ou 'bottom-right' */
  pageNumberPosition?: string;
  /** Margem superior (mm) quando há plano de fundo */
  bgMarginTopMm?: number;
  /** Margem inferior (mm) quando há plano de fundo */
  bgMarginBottomMm?: number;
  /** Offset vertical (mm) da paginação em relação à borda */
  paginationYOffsetMm?: number;
  /** Altura do container scrollável (default: 500px). Use 'h-full' para flex pais. */
  className?: string;
}

export function ContractFullPreview({
  contentHtml,
  backgroundImagePath,
  coverPdfPath,
  basePdfPath,
  basePdfPosition = 'before',
  showPageNumbers,
  pageNumberPosition,
  bgMarginTopMm,
  bgMarginBottomMm,
  paginationYOffsetMm,
  className,
}: ContractFullPreviewProps) {
  // -----------------------------------------------------------------------
  // Resolve as URLs reais dos assets (signed URL ou URL pública direta)
  // -----------------------------------------------------------------------
  const backgroundUrl = useContractAssetUrl(backgroundImagePath ?? null);
  const coverUrl = useContractAssetUrl(coverPdfPath ?? null);
  const baseUrl = useContractAssetUrl(basePdfPath ?? null);

  // -----------------------------------------------------------------------
  // Escala dinâmica baseada na largura do container externo
  // -----------------------------------------------------------------------
  const outerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState<number>(PREVIEW_SCALE_FACTOR);

  useEffect(() => {
    if (!outerRef.current) return;

    const update = () => {
      const w = outerRef.current?.clientWidth ?? 0;
      setScale(getPreviewScaleFactor(w));
    };
    update();

    const ro = new ResizeObserver(update);
    ro.observe(outerRef.current);
    return () => ro.disconnect();
  }, []);

  // -----------------------------------------------------------------------
  // Estado vazio
  // -----------------------------------------------------------------------
  if (!contentHtml && !coverPdfPath && !basePdfPath) {
    return (
      <div className="border rounded-lg p-8 text-center text-muted-foreground min-h-[300px] flex items-center justify-center">
        <p className="text-sm">Nenhum conteúdo disponível para pré-visualização.</p>
      </div>
    );
  }

  // Monta a lista de blocos na ordem correta
  const showCover = !!coverUrl;
  const showBase = !!baseUrl;

  // Texto descritivo no header
  const parts: string[] = [];
  if (showCover) parts.push('capa');
  parts.push('conteúdo');
  if (showBase) parts.push('cláusulas fixas');
  const composition = parts.join(' + ');

  return (
    <div ref={outerRef} className={`border rounded-lg overflow-hidden w-full flex flex-col ${className ?? ''}`}>
      {/* Cabeçalho informativo único */}
      <div className="flex items-center gap-2 px-3 sm:px-4 py-2 border-b bg-muted/30 shrink-0">
        <Badge variant="outline" className="text-[11px] sm:text-xs">Pré-visualização PDF</Badge>
        <span className="text-[11px] sm:text-xs text-muted-foreground truncate">
          Réplica fiel — {composition}
        </span>
      </div>

      {/* Área de scroll com fundo "mesa de trabalho" */}
      <ScrollArea
        className={className?.includes('h-') ? 'flex-1' : 'h-[500px]'}
        style={{ backgroundColor: '#e5e5e5' }}
      >
        <div
          style={{
            backgroundColor: '#e5e5e5',
            padding: '24px 16px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: `${24 * scale}px`,
            minHeight: '100%',
            position: 'relative',
          }}
        >
          {/* 1. Capa (sempre primeiro) */}
          {showCover && coverUrl && (
            <ContractPdfPagePreview url={coverUrl} label="Capa" scale={scale} visualPages={1} />
          )}

          {/* 2. PDF base ANTES do anexo HTML */}
          {showBase && baseUrl && basePdfPosition === 'before' && (
            <ContractPdfPagePreview url={baseUrl} label="PDF base" scale={scale} visualPages={1} />
          )}

          {/* 3. Conteúdo HTML paginado (delegado ao componente existente em modo embed) */}
          {contentHtml && (
            <ContractTemplatePreview
              contentHtml={contentHtml}
              backgroundPreviewUrl={backgroundUrl}
              showPageNumbers={showPageNumbers}
              pageNumberPosition={pageNumberPosition}
              bgMarginTopMm={bgMarginTopMm}
              bgMarginBottomMm={bgMarginBottomMm}
              paginationYOffsetMm={paginationYOffsetMm}
              embed
              embedScale={scale}
            />
          )}

          {/* 4. PDF base DEPOIS do anexo HTML */}
          {showBase && baseUrl && basePdfPosition === 'after' && (
            <ContractPdfPagePreview url={baseUrl} label="PDF base" scale={scale} visualPages={1} />
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
